import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Star, Quote, ChevronLeft, ChevronRight, Globe, Instagram, Linkedin, Facebook, Mail, MessageCircle, Youtube, Briefcase, Sparkles } from "lucide-react";
import { useReviews } from "@/lib/ReviewsProvider";

// Source -> icon + label + brand color tone (subtle, on-theme).
const SOURCE_META = {
    google:    { label: "Google",    Icon: Globe,         color: "#E9B86F" },
    instagram: { label: "Instagram", Icon: Instagram,     color: "#E78CB3" },
    behance:   { label: "Behance",   Icon: Briefcase,     color: "#6FB1FF" },
    facebook:  { label: "Facebook",  Icon: Facebook,      color: "#7AA8FF" },
    linkedin:  { label: "LinkedIn",  Icon: Linkedin,      color: "#7AC0FF" },
    upwork:    { label: "Upwork",    Icon: Briefcase,     color: "#7FE3B0" },
    fiverr:    { label: "Fiverr",    Icon: Briefcase,     color: "#7FE3B0" },
    email:     { label: "Email",     Icon: Mail,          color: "#C9C9D1" },
    direct:    { label: "Client",    Icon: MessageCircle, color: "#C9C9D1" },
    twitter:   { label: "X / Twitter", Icon: MessageCircle, color: "#C9C9D1" },
    youtube:   { label: "YouTube",   Icon: Youtube,       color: "#F3A0A0" },
};

const getSourceMeta = (src) => SOURCE_META[(src || "").toLowerCase()] || SOURCE_META.direct;

const initialsOf = (name) =>
    (name || "")
        .split(" ")
        .filter(Boolean)
        .slice(0, 2)
        .map((s) => s[0]?.toUpperCase() || "")
        .join("");

const Stars = ({ rating }) => {
    if (!rating || rating < 1) return null;
    return (
        <div className="flex items-center gap-0.5" aria-label={`${rating} out of 5 stars`} data-testid="review-stars">
            {Array.from({ length: 5 }).map((_, i) => (
                <Star
                    key={i}
                    className="h-3.5 w-3.5"
                    style={{
                        color: i < rating ? "var(--pg-accent)" : "color-mix(in srgb, var(--pg-fg) 14%, transparent)",
                        fill: i < rating ? "var(--pg-accent)" : "transparent",
                    }}
                    strokeWidth={1.4}
                />
            ))}
        </div>
    );
};

const Avatar = ({ name, photo }) => {
    if (photo) {
        return (
            <img
                src={photo}
                alt={`${name} avatar`}
                className="h-12 w-12 shrink-0 rounded-full object-cover"
                style={{ border: "1px solid var(--pg-border)" }}
                onError={(e) => { e.target.style.display = "none"; e.target.nextSibling.style.display = "flex"; }}
                loading="lazy"
            />
        );
    }
    return (
        <span
            className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-full text-sm font-display"
            style={{
                background: "color-mix(in srgb, var(--pg-accent) 16%, var(--pg-surface-2))",
                color: "var(--pg-fg)",
                border: "1px solid var(--pg-border)",
            }}
            aria-hidden
        >
            {initialsOf(name) || "·"}
        </span>
    );
};

const ReviewCard = ({ review, index }) => {
    const meta = getSourceMeta(review.source);
    const SourceIcon = meta.Icon;
    const ref = useRef(null);
    const [hover, setHover] = useState(false);

    // Cursor-glow tracking — purely CSS-var driven so React doesn't re-render.
    const handleMove = useCallback((e) => {
        const el = ref.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const mx = ((e.clientX - rect.left) / rect.width) * 100;
        const my = ((e.clientY - rect.top) / rect.height) * 100;
        el.style.setProperty("--rgx", `${mx}%`);
        el.style.setProperty("--rgy", `${my}%`);
    }, []);
    const handleEnter = useCallback(() => setHover(true), []);
    const handleLeave = useCallback(() => {
        setHover(false);
        const el = ref.current;
        if (!el) return;
        el.style.setProperty("--rgx", `50%`);
        el.style.setProperty("--rgy", `50%`);
    }, []);

    return (
        <motion.article
            ref={ref}
            onMouseMove={handleMove}
            onMouseEnter={handleEnter}
            onMouseLeave={handleLeave}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7, delay: index * 0.08, ease: [0.16, 1, 0.3, 1] }}
            whileHover={{ y: -6, transition: { duration: 0.4, ease: [0.16, 1, 0.3, 1] } }}
            className="pg-review-card pg-surface-ivory relative flex h-full flex-col overflow-hidden rounded-3xl border p-7 sm:p-8"
            data-testid="review-card"
            data-review-id={review.id}
            style={{
                "--rgx": "50%",
                "--rgy": "50%",
                background: "var(--pg-surface)",
                borderColor: hover ? "color-mix(in srgb, var(--pg-accent) 38%, var(--pg-border))" : "var(--pg-border)",
                boxShadow: hover
                    ? "0 32px 70px -28px rgba(217, 160, 64, 0.28), 0 12px 36px -16px rgba(0,0,0,0.25)"
                    : "0 24px 48px -28px rgba(0,0,0,0.18)",
                transition: "border-color 360ms ease, box-shadow 360ms ease",
            }}
        >
            {/* Cursor-following gold glow — invisible until hover, then fades in */}
            <span
                aria-hidden
                className="pointer-events-none absolute inset-0 transition-opacity duration-500"
                style={{
                    background:
                        "radial-gradient(420px circle at var(--rgx) var(--rgy), color-mix(in srgb, var(--pg-accent) 22%, transparent), transparent 60%)",
                    opacity: hover ? 1 : 0,
                    mixBlendMode: "screen",
                }}
            />

            {/* Static soft gold corner accent (always-on, very subtle) */}
            <span
                aria-hidden
                className="pointer-events-none absolute -right-12 -top-12 h-44 w-44 rounded-full"
                style={{
                    background:
                        "radial-gradient(closest-side, color-mix(in srgb, var(--pg-accent) 30%, transparent), transparent 70%)",
                    filter: "blur(10px)",
                }}
            />

            <div className="relative z-10 flex items-center justify-between gap-3">
                <motion.span
                    className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5"
                    style={{
                        borderColor: "var(--pg-border-strong)",
                        background: "var(--pg-surface-2)",
                        color: "var(--pg-fg-muted)",
                    }}
                    animate={{
                        borderColor: hover
                            ? "color-mix(in srgb, var(--pg-accent) 38%, transparent)"
                            : "var(--pg-border-strong)",
                    }}
                    transition={{ duration: 0.35 }}
                >
                    <SourceIcon className="h-3 w-3" style={{ color: meta.color }} strokeWidth={1.8} />
                    <span className="font-mono text-[10px] uppercase tracking-[0.18em]">{meta.label}</span>
                </motion.span>
                <Stars rating={review.rating} />
            </div>

            <motion.div
                className="relative z-10 mt-6"
                animate={{
                    rotate: hover ? -6 : 0,
                    scale: hover ? 1.08 : 1,
                }}
                transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            >
                <Quote
                    className="h-7 w-7"
                    style={{ color: "color-mix(in srgb, var(--pg-accent) 75%, transparent)" }}
                    strokeWidth={1.4}
                    aria-hidden
                />
            </motion.div>

            <p
                className="relative z-10 mt-3 flex-1 text-[15px] leading-relaxed sm:text-base sm:leading-relaxed"
                style={{ color: "var(--pg-fg)" }}
            >
                “{review.quote}”
            </p>

            {review.project && (
                <p
                    className="relative z-10 mt-4 font-mono text-[10px] uppercase tracking-[0.22em]"
                    style={{ color: "var(--pg-fg-muted)" }}
                >
                    Project · {review.project}
                </p>
            )}

            <div
                className="relative z-10 mt-6 flex items-center gap-3 border-t pt-5"
                style={{ borderColor: "var(--pg-border)" }}
            >
                <Avatar name={review.name} photo={review.photo} />
                <div className="min-w-0">
                    <div
                        className="font-display text-base truncate"
                        style={{ color: "var(--pg-fg)" }}
                        data-testid="review-author-name"
                    >
                        {review.name}
                    </div>
                    <div className="text-[12px] truncate" style={{ color: "var(--pg-fg-muted)" }}>
                        {[review.role, review.location].filter(Boolean).join(" · ")}
                    </div>
                </div>
            </div>
        </motion.article>
    );
};

export const Reviews = () => {
    const { reviews, loaded } = useReviews();
    const [page, setPage] = useState(0);
    const [perPage, setPerPage] = useState(3);

    useEffect(() => {
        const updatePerPage = () => {
            if (typeof window === "undefined") return;
            const w = window.innerWidth;
            if (w < 640) setPerPage(1);
            else if (w < 1024) setPerPage(2);
            else setPerPage(3);
        };
        updatePerPage();
        window.addEventListener("resize", updatePerPage);
        return () => window.removeEventListener("resize", updatePerPage);
    }, []);

    const totalPages = Math.max(1, Math.ceil(reviews.length / perPage));
    const pageItems = useMemo(
        () => reviews.slice(page * perPage, page * perPage + perPage),
        [reviews, page, perPage]
    );

    // Reset page when perPage changes
    useEffect(() => { setPage(0); }, [perPage]);

    // Auto-advance every 8s if there are multiple pages
    useEffect(() => {
        if (totalPages <= 1) return;
        const t = setInterval(() => setPage((p) => (p + 1) % totalPages), 8000);
        return () => clearInterval(t);
    }, [totalPages]);

    const goPrev = useCallback(() => setPage((p) => (p - 1 + totalPages) % totalPages), [totalPages]);
    const goNext = useCallback(() => setPage((p) => (p + 1) % totalPages), [totalPages]);

    if (loaded && reviews.length === 0) {
        // Render nothing if no reviews have been added — the section stays hidden.
        return null;
    }

    const aggregateAvg = (() => {
        const rated = reviews.filter((r) => (r.rating || 0) > 0);
        if (!rated.length) return null;
        const sum = rated.reduce((a, r) => a + (r.rating || 0), 0);
        return (sum / rated.length).toFixed(1);
    })();

    return (
        <section
            id="reviews"
            className="pg-section pg-surface-ink relative overflow-hidden"
            aria-label="Client reviews and testimonials"
            data-testid="reviews-section"
        >
            {/* Ambient roaming glow backdrop — slowly drifts in two opposing paths.
                Same cinematic motion vocabulary as the hero, but quieter. */}
            <motion.div
                aria-hidden
                className="pointer-events-none absolute -left-32 top-20 h-[42rem] w-[42rem] rounded-full"
                style={{
                    background:
                        "radial-gradient(closest-side, color-mix(in srgb, var(--pg-accent) 18%, transparent), transparent 70%)",
                    filter: "blur(60px)",
                    mixBlendMode: "screen",
                }}
                initial={{ x: 0, y: 0 }}
                animate={{ x: ["0%", "30%", "10%", "-5%", "0%"], y: ["0%", "10%", "30%", "20%", "0%"] }}
                transition={{ duration: 28, times: [0, 0.25, 0.5, 0.75, 1], repeat: Infinity, ease: "easeInOut" }}
            />
            <motion.div
                aria-hidden
                className="pointer-events-none absolute -right-40 bottom-10 h-[36rem] w-[36rem] rounded-full"
                style={{
                    background:
                        "radial-gradient(closest-side, color-mix(in srgb, var(--pg-accent-2, var(--pg-accent)) 16%, transparent), transparent 70%)",
                    filter: "blur(70px)",
                    mixBlendMode: "screen",
                }}
                initial={{ x: 0, y: 0 }}
                animate={{ x: ["0%", "-20%", "-30%", "-10%", "0%"], y: ["0%", "-15%", "-30%", "-10%", "0%"] }}
                transition={{ duration: 28, times: [0, 0.25, 0.5, 0.75, 1], repeat: Infinity, ease: "easeInOut", delay: 4 }}
            />

            <div className="pg-container relative z-10">
                <motion.div
                    initial={{ opacity: 0, y: 18 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                    className="flex flex-wrap items-end justify-between gap-6"
                >
                    <div className="max-w-2xl">
                        <span className="pg-eyebrow inline-flex items-center gap-2">
                            <Sparkles className="h-3 w-3" style={{ color: "var(--pg-accent)" }} />
                            Reviews · Client Voices
                        </span>
                        <h2
                            className="font-display mt-5 text-[clamp(2rem,4.6vw,3.4rem)] leading-[1.05]"
                            style={{ color: "var(--pg-fg)" }}
                        >
                            What clients say after
                            <br className="hidden sm:block" />
                            we&apos;ve <span className="pg-text-gold-static">shipped the brand</span>.
                        </h2>
                    </div>

                    <div className="flex flex-wrap items-center gap-4">
                        {aggregateAvg && (
                            <div
                                className="flex items-center gap-3 rounded-full border px-4 py-2.5"
                                style={{
                                    borderColor: "var(--pg-border)",
                                    background: "var(--pg-surface)",
                                }}
                                data-testid="reviews-aggregate"
                            >
                                <Stars rating={Math.round(parseFloat(aggregateAvg))} />
                                <div className="flex items-baseline gap-1.5">
                                    <span
                                        className="font-display text-lg"
                                        style={{ color: "var(--pg-fg)" }}
                                    >
                                        {aggregateAvg}
                                    </span>
                                    <span className="font-mono text-[10px] uppercase tracking-[0.22em]" style={{ color: "var(--pg-fg-muted)" }}>
                                        / 5 · {reviews.length} review{reviews.length !== 1 ? "s" : ""}
                                    </span>
                                </div>
                            </div>
                        )}
                        {totalPages > 1 && (
                            <div className="flex items-center gap-2">
                                <button
                                    type="button"
                                    onClick={goPrev}
                                    aria-label="Previous reviews"
                                    data-testid="reviews-prev"
                                    className="pg-app-tap inline-flex h-10 w-10 items-center justify-center rounded-full border transition-colors hover:bg-[var(--pg-surface-2)]"
                                    style={{ borderColor: "var(--pg-border-strong)", color: "var(--pg-fg)" }}
                                >
                                    <ChevronLeft className="h-4 w-4" />
                                </button>
                                <button
                                    type="button"
                                    onClick={goNext}
                                    aria-label="Next reviews"
                                    data-testid="reviews-next"
                                    className="pg-app-tap inline-flex h-10 w-10 items-center justify-center rounded-full border transition-colors hover:bg-[var(--pg-surface-2)]"
                                    style={{ borderColor: "var(--pg-border-strong)", color: "var(--pg-fg)" }}
                                >
                                    <ChevronRight className="h-4 w-4" />
                                </button>
                            </div>
                        )}
                    </div>
                </motion.div>

                <div className="mt-12 min-h-[1px]">
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={page}
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                            className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3"
                        >
                            {pageItems.map((r, i) => (
                                <ReviewCard key={r.id} review={r} index={i} />
                            ))}
                        </motion.div>
                    </AnimatePresence>
                </div>

                {/* Dot pagination */}
                {totalPages > 1 && (
                    <div
                        className="mt-8 flex items-center justify-center gap-2"
                        role="tablist"
                        aria-label="Review pages"
                        data-testid="reviews-dots"
                    >
                        {Array.from({ length: totalPages }).map((_, i) => {
                            const active = i === page;
                            return (
                                <button
                                    key={i}
                                    type="button"
                                    onClick={() => setPage(i)}
                                    aria-label={`Go to review page ${i + 1}`}
                                    aria-selected={active}
                                    role="tab"
                                    className="pg-app-tap h-2 rounded-full transition-[width,background-color] duration-300"
                                    style={{
                                        width: active ? 24 : 8,
                                        background: active
                                            ? "var(--pg-accent)"
                                            : "color-mix(in srgb, var(--pg-fg) 18%, transparent)",
                                    }}
                                />
                            );
                        })}
                    </div>
                )}
            </div>
        </section>
    );
};

export default Reviews;
